import {
  toWSDateString,
  WSTableCell,
  WSTableColumn,
  WSTableColumnSortDirection
} from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { Currency } from "@wingspanhq/utils/dist/currency/currencies";
import React from "react";
import {
  Payable,
  PayablesSortField
} from "../../../../services/api/payments/payables";
import { getPayableStatusCellProps } from "./getStatusColumn";

export type ColumnKey =
  | "payee"
  | "payableNumber"
  | "amount"
  | "dueDate"
  | "openedAt"
  | "paidAt"
  | "status";

export type PayeeDisplayInfo = {
  name?: string;
  companyName?: string;
  email?: string;
};

const getPayeeIdFallback = (payeeId?: string): string =>
  payeeId ? payeeId.slice(-8) : "";

const getPayeePrimaryText = (info: PayeeDisplayInfo): string | undefined =>
  info.name ?? info.companyName;

export const DEFAULT_VISIBLE_COLUMNS: Record<ColumnKey, boolean> = {
  payee: true,
  payableNumber: false,
  amount: true,
  dueDate: false,
  openedAt: false,
  paidAt: false,
  status: true
};

type GetColumnsOptions = {
  visibleColumns?: Partial<Record<ColumnKey, boolean>>;
  payeesMap?: Map<string, PayeeDisplayInfo>;
  sortField?: PayablesSortField;
  sortDirection?: WSTableColumnSortDirection;
  // Customizable label for the paid-date column (terminology.sendPaymentsPaidAt).
  paidAtLabel?: string;
  onSortChange?: (
    field: PayablesSortField,
    direction: WSTableColumnSortDirection
  ) => void;
};

const buildSortHandler = (
  field: PayablesSortField,
  options: GetColumnsOptions
) => {
  if (!options.onSortChange) {
    return undefined;
  }
  return (direction: WSTableColumnSortDirection) =>
    options.onSortChange!(field, direction);
};

const sortDirectionFor = (
  field: PayablesSortField,
  options: GetColumnsOptions
): WSTableColumnSortDirection | undefined =>
  options.sortField === field ? options.sortDirection : undefined;

export const getColumns = (
  options: GetColumnsOptions
): WSTableColumn<Payable>[] => {
  const visible = { ...DEFAULT_VISIBLE_COLUMNS, ...options.visibleColumns };

  const payeeColumn: WSTableColumn<Payable> = {
    config: {
      gridTemplateSizeMax: "2fr",
      header: { text: "Name" }
    },
    renderFunction: item => {
      const payable = item.data;
      const info = payable.payeeId
        ? options.payeesMap?.get(payable.payeeId)
        : undefined;

      if (info) {
        const primary = getPayeePrimaryText(info);
        return (
          <WSTableCell
            text={primary}
            secondaryText={info.email}
            truncationOptions={{ text: { allowTwoLines: true } }}
          />
        );
      }

      const fallbackSecondary = getPayeeIdFallback(payable.payeeId);

      return (
        <WSTableCell
          text="Contractor"
          secondaryText={fallbackSecondary || undefined}
          truncationOptions={{ text: { allowTwoLines: true } }}
        />
      );
    }
  };

  const payableNumberColumn: WSTableColumn<Payable> = {
    config: {
      gridTemplateSizeMax: "1fr",
      hideOnScreens: ["XS", "S"],
      header: { text: "Invoice #" }
    },
    renderFunction: item => (
      <WSTableCell
        text={item.data.payableNumber}
        secondaryText={item.data.lineItems?.[0]?.description}
      />
    )
  };

  const amountColumn: WSTableColumn<Payable> = {
    config: {
      header: { text: "Amount" }
    },
    renderFunction: item => {
      const payable = item.data;
      if (payable.amount === undefined) {
        return <WSTableCell text="-" />;
      }
      return (
        <WSTableCell
          text={formatMoney(payable.amount, payable.currency as Currency)}
        />
      );
    }
  };

  // dueDate is NOT in the sortable list per PayablesSortField, so it stays
  // non-sortable here even though it backs `internal.events.paymentDueAt`
  // sorting elsewhere.
  const dueDateColumn: WSTableColumn<Payable> = {
    config: {
      gridTemplateSizeMax: "1fr",
      hideOnScreens: ["XS", "S"],
      header: { text: "Due date" }
    },
    renderFunction: item => (
      <WSTableCell text={toWSDateString(item.data.dueDate, "monthDayYear")} />
    )
  };

  const openedAtColumn: WSTableColumn<Payable> = {
    config: {
      gridTemplateSizeMax: "1fr",
      hideOnScreens: ["XS", "S"],
      header: { text: "Opened date" }
    },
    // The "Eligibility pending" label belongs to `Pending` payables only —
    // they are the ones genuinely blocked from opening by an unmet
    // requirement. Every other unopened state (Draft above all) has no opened
    // date to show, so it renders the empty-date placeholder, matching legacy.
    renderFunction: item => {
      const { status, events } = item.data;
      return (
        <WSTableCell
          text={
            status === "Pending"
              ? "Eligibility pending"
              : toWSDateString(events?.openedAt, "monthDayYear")
          }
        />
      );
    }
  };

  const paidAtColumn: WSTableColumn<Payable> = {
    config: {
      gridTemplateSizeMax: "1fr",
      hideOnScreens: ["XS", "S"],
      header: { text: options.paidAtLabel ?? "Processed (paid)" },
      onColumnSort: buildSortHandler("events.paidAt", options),
      sortDirection: sortDirectionFor("events.paidAt", options)
    },
    renderFunction: item => (
      <WSTableCell
        text={toWSDateString(item.data.events?.paidAt, "monthDayYear")}
      />
    )
  };

  const statusColumn: WSTableColumn<Payable> = {
    config: {
      gridTemplateSizeMax: "1.5fr",
      header: { text: "Status" }
    },
    renderFunction: item => (
      <WSTableCell {...getPayableStatusCellProps(item.data)} />
    )
  };

  const all: Array<{ key: ColumnKey; column: WSTableColumn<Payable> }> = [
    { key: "payee", column: payeeColumn },
    { key: "payableNumber", column: payableNumberColumn },
    { key: "amount", column: amountColumn },
    { key: "openedAt", column: openedAtColumn },
    { key: "dueDate", column: dueDateColumn },
    { key: "paidAt", column: paidAtColumn },
    { key: "status", column: statusColumn }
  ];

  return all.filter(c => visible[c.key]).map(c => c.column);
};
